// src/modules/ai-agent/AIAgentDashboard.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../layouts/DashboardLayout';
import { getConfig, updateConfig, getVoiceConfig, updateVoiceConfig, getPrompt, updatePrompt, getFeatureFlags, updateFeatureFlags } from './aiAgentService';
import { FaRobot, FaSave, FaMicrophone, FaCommentDots, FaToggleOn, FaToggleOff, FaTools, FaInfoCircle, FaChevronDown, FaChevronUp, FaCog } from 'react-icons/fa';

const Container = styled.div`
  padding: 20px;
  max-width: 900px;
`;

const PageTitle = styled.h1`
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Section = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 6px;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface};
  border-radius: 6px;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.95rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface};
  border-radius: 6px;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.95rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SaveButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${props => props.theme.colors.background};

  &:last-child {
    border-bottom: none;
  }
`;

const ToggleLabel = styled.div`
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.95rem;
`;

const ToggleDescription = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.8rem;
  margin-top: 2px;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.8rem;
  color: ${props => props.isOn ? props.theme.colors.primary : props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Message = styled.div`
  padding: 10px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  background-color: ${props => props.error ? '#fa7f7f22' : '#4caf5022'};
  color: ${props => props.error ? '#fa7f7f' : '#4caf50'};
  font-size: 0.9rem;
`;

const HelpText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.82rem;
  margin-top: 6px;
  line-height: 1.5;
`;

const Collapsible = styled.div`
  border: 1px solid ${props => props.theme.colors.inputGray}33;
  border-radius: 6px;
  margin-bottom: 12px;
  overflow: hidden;
`;

const CollapsibleHeader = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${props => props.theme.colors.background};
  border: none;
  color: ${props => props.theme.colors.textPrimary};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;

  &:hover {
    background: ${props => props.theme.colors.background}dd;
  }

  svg {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const CollapsibleBody = styled.div`
  padding: ${props => props.isOpen ? '16px' : '0 16px'};
  max-height: ${props => props.isOpen ? '2000px' : '0'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  background: ${props => props.theme.colors.background}88;
`;

const ToolTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${props => props.color || props.theme.colors.primary}18;
  color: ${props => props.color || props.theme.colors.primary};
  border: 1px solid ${props => props.color || props.theme.colors.primary}33;
  border-radius: 14px;
  font-size: 0.78rem;
  margin: 3px;
  font-family: monospace;
`;

const ToolGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
`;

const PlaceholderTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  font-size: 0.82rem;

  th, td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid ${props => props.theme.colors.inputGray}22;
  }

  th {
    color: ${props => props.theme.colors.textSecondary};
    font-weight: 600;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  td {
    color: ${props => props.theme.colors.textPrimary};
  }

  code {
    background: ${props => props.theme.colors.background};
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 0.8rem;
    color: ${props => props.theme.colors.primary};
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  border-bottom: 2px solid ${props => props.theme.colors.inputGray}22;
`;

const Tab = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.isActive ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.textSecondary};
  font-size: 0.9rem;
  font-weight: ${props => props.isActive ? '600' : '400'};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: -2px;
  transition: all 0.2s ease;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const CharCount = styled.span`
  display: block;
  text-align: right;
  font-size: 0.75rem;
  color: ${props => props.warning ? '#f39c12' : props.theme.colors.textSecondary};
  margin-top: 4px;
`;

const AIAgentDashboard = () => {
  const [flags, setFlags] = useState({});
  const [voice, setVoice] = useState({});
  const [prompt, setPrompt] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [message, setMessage] = useState(null);
  const [promptTab, setPromptTab] = useState('instructions');
  const [openSections, setOpenSections] = useState({ tools: false, placeholders: false, tips: false });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [f, v, p] = await Promise.all([getFeatureFlags(), getVoiceConfig(), getPrompt()]);
      setFlags(f);
      setVoice(v);
      setPrompt(p);
    } catch (err) {
      console.error('Error loading AI agent config:', err);
      setMessage({ text: 'Error loading configuration', error: true });
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, error = false) => {
    setMessage({ text, error });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleSaveFlags = async () => {
    try {
      setSaving('flags');
      await updateFeatureFlags(flags);
      showMessage('Settings saved successfully');
    } catch (err) {
      showMessage('Error saving settings', true);
    } finally {
      setSaving('');
    }
  };

  const handleSaveVoice = async () => {
    try {
      setSaving('voice');
      await updateVoiceConfig(voice);
      showMessage('Voice settings saved');
    } catch (err) {
      showMessage('Error saving voice settings', true);
    } finally {
      setSaving('');
    }
  };

  const handleSavePrompt = async () => {
    try {
      setSaving('prompt');
      await updatePrompt(prompt);
      showMessage('Prompt saved');
    } catch (err) {
      showMessage('Error saving prompt', true);
    } finally {
      setSaving('');
    }
  };

  const toggleFlag = (key) => {
    setFlags(prev => ({ ...prev, [key]: prev[key] === 'true' ? 'false' : 'true' }));
  };

  const toggleSection = (key) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return <DashboardLayout><Container><p style={{ color: '#ccc' }}>Loading...</p></Container></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <Container>
        <PageTitle><FaRobot /> AI Voice Agent</PageTitle>

        {message && <Message error={message.error}>{message.text}</Message>}

        {/* Feature Flags */}
        <Section>
          <SectionTitle><FaToggleOn /> Agent Settings</SectionTitle>

          <ToggleRow>
            <div>
              <ToggleLabel>AI Agent Enabled</ToggleLabel>
              <ToggleDescription>When ON, press-1 callers connect to the AI voice agent instead of forwarding</ToggleDescription>
            </div>
            <ToggleButton isOn={flags.ai_agent_enabled === 'true'} onClick={() => toggleFlag('ai_agent_enabled')}>
              {flags.ai_agent_enabled === 'true' ? <FaToggleOn /> : <FaToggleOff />}
            </ToggleButton>
          </ToggleRow>

          <ToggleRow>
            <div>
              <ToggleLabel>Database-Driven IVR Menu</ToggleLabel>
              <ToggleDescription>When ON, the phone menu is loaded from the IVR Menu page instead of hardcoded</ToggleDescription>
            </div>
            <ToggleButton isOn={flags.use_db_ivr_menu === 'true'} onClick={() => toggleFlag('use_db_ivr_menu')}>
              {flags.use_db_ivr_menu === 'true' ? <FaToggleOn /> : <FaToggleOff />}
            </ToggleButton>
          </ToggleRow>

          <Row style={{ marginTop: '16px' }}>
            <FormGroup>
              <Label>Max Concurrent Calls</Label>
              <Input type="number" value={flags.max_concurrent_calls || ''} onChange={e => setFlags(prev => ({ ...prev, max_concurrent_calls: e.target.value }))} />
            </FormGroup>
            <FormGroup>
              <Label>Max Call Duration (seconds)</Label>
              <Input type="number" value={flags.max_call_duration_sec || ''} onChange={e => setFlags(prev => ({ ...prev, max_call_duration_sec: e.target.value }))} />
            </FormGroup>
          </Row>

          <Row>
            <FormGroup>
              <Label>LLM Model</Label>
              <Input value={flags.llm_model || ''} onChange={e => setFlags(prev => ({ ...prev, llm_model: e.target.value }))} />
            </FormGroup>
            <FormGroup>
              <Label>Transfer Number</Label>
              <Input value={flags.transfer_number || ''} placeholder="Defaults to main forward number" onChange={e => setFlags(prev => ({ ...prev, transfer_number: e.target.value }))} />
            </FormGroup>
          </Row>

          <SaveButton onClick={handleSaveFlags} disabled={saving === 'flags'}>
            <FaSave /> {saving === 'flags' ? 'Saving...' : 'Save Settings'}
          </SaveButton>
        </Section>

        {/* Voice Config */}
        <Section>
          <SectionTitle><FaMicrophone /> Voice Configuration</SectionTitle>

          <Row>
            <FormGroup>
              <Label>ElevenLabs Voice ID</Label>
              <Input value={voice.elevenlabs_voice_id || ''} onChange={e => setVoice(prev => ({ ...prev, elevenlabs_voice_id: e.target.value }))} />
            </FormGroup>
            <FormGroup>
              <Label>ElevenLabs Model</Label>
              <Input value={voice.elevenlabs_model || ''} onChange={e => setVoice(prev => ({ ...prev, elevenlabs_model: e.target.value }))} />
            </FormGroup>
          </Row>

          <Row>
            <FormGroup>
              <Label>Stability (0-1)</Label>
              <Input type="number" step="0.1" min="0" max="1" value={voice.voice_stability || ''} onChange={e => setVoice(prev => ({ ...prev, voice_stability: e.target.value }))} />
            </FormGroup>
            <FormGroup>
              <Label>Similarity (0-1)</Label>
              <Input type="number" step="0.1" min="0" max="1" value={voice.voice_similarity || ''} onChange={e => setVoice(prev => ({ ...prev, voice_similarity: e.target.value }))} />
            </FormGroup>
          </Row>

          <SaveButton onClick={handleSaveVoice} disabled={saving === 'voice'}>
            <FaSave /> {saving === 'voice' ? 'Saving...' : 'Save Voice'}
          </SaveButton>
        </Section>

        {/* Prompt Settings */}
        <Section>
          <SectionTitle><FaCommentDots /> Prompt & Instructions</SectionTitle>

          <Tabs>
            <Tab isActive={promptTab === 'instructions'} onClick={() => setPromptTab('instructions')}>
              <FaCommentDots /> Instructions
            </Tab>
            <Tab isActive={promptTab === 'tools'} onClick={() => setPromptTab('tools')}>
              <FaTools /> Tools & Capabilities
            </Tab>
            <Tab isActive={promptTab === 'context'} onClick={() => setPromptTab('context')}>
              <FaCog /> Business Context
            </Tab>
          </Tabs>

          {/* ── Instructions Tab ── */}
          {promptTab === 'instructions' && (
            <>
              <FormGroup>
                <Label>Greeting Message</Label>
                <HelpText>The first thing the caller hears when connected to the AI agent. Keep it short and welcoming.</HelpText>
                <Input
                  value={prompt.greeting_message || ''}
                  onChange={e => setPrompt(prev => ({ ...prev, greeting_message: e.target.value }))}
                  placeholder="e.g. Hello! Thank you for calling Mr. Patch Asphalt. How can I help you today?"
                />
                <CharCount>{(prompt.greeting_message || '').length} characters</CharCount>
              </FormGroup>

              <FormGroup>
                <Label>System Prompt (Main Instructions)</Label>
                <HelpText>
                  This is the core instruction set sent to the LLM. It defines the agent's personality, behavior, rules, and how it should handle calls.
                  Be specific about what the agent should and should NOT do.
                </HelpText>
                <TextArea
                  rows={14}
                  value={prompt.system_prompt || ''}
                  onChange={e => setPrompt(prev => ({ ...prev, system_prompt: e.target.value }))}
                  placeholder={`e.g. You are a friendly and professional phone assistant for Mr. Patch Asphalt, a paving and asphalt repair company.\n\nYour job is to:\n- Answer questions about our services\n- Collect caller information (name, phone, address, service needed)\n- Schedule estimates when requested\n- Transfer to a human agent when asked\n\nRules:\n- Always be polite and professional\n- Never make up pricing - say you'll need to schedule an estimate\n- If you don't know something, offer to transfer to a team member`}
                />
                <CharCount warning={(prompt.system_prompt || '').length > 4000}>
                  {(prompt.system_prompt || '').length} characters
                  {(prompt.system_prompt || '').length > 4000 && ' - Consider shortening for better performance'}
                </CharCount>
              </FormGroup>

              {/* Reference: Available Placeholders */}
              <Collapsible>
                <CollapsibleHeader onClick={() => toggleSection('placeholders')}>
                  <span><FaInfoCircle /> Available Placeholders</span>
                  {openSections.placeholders ? <FaChevronUp /> : <FaChevronDown />}
                </CollapsibleHeader>
                <CollapsibleBody isOpen={openSections.placeholders}>
                  <HelpText>Use these placeholders in your system prompt. They are automatically replaced with real data at runtime.</HelpText>
                  <PlaceholderTable>
                    <thead>
                      <tr><th>Placeholder</th><th>Replaced With</th></tr>
                    </thead>
                    <tbody>
                      <tr><td><code>{'{{BUSINESS_NAME}}'}</code></td><td>Your company name</td></tr>
                      <tr><td><code>{'{{BUSINESS_PHONE}}'}</code></td><td>Your main phone number</td></tr>
                      <tr><td><code>{'{{BUSINESS_HOURS}}'}</code></td><td>Operating hours from settings</td></tr>
                      <tr><td><code>{'{{SERVICE_AREAS}}'}</code></td><td>List of cities/zipcodes you serve</td></tr>
                      <tr><td><code>{'{{SERVICES_LIST}}'}</code></td><td>List of services you offer</td></tr>
                      <tr><td><code>{'{{CONTEXT_INFO}}'}</code></td><td>Business context from the next tab</td></tr>
                      <tr><td><code>{'{{CURRENT_DATE}}'}</code></td><td>Today's date</td></tr>
                      <tr><td><code>{'{{CALLER_NUMBER}}'}</code></td><td>The incoming caller's phone number</td></tr>
                    </tbody>
                  </PlaceholderTable>
                </CollapsibleBody>
              </Collapsible>

              {/* Tips */}
              <Collapsible>
                <CollapsibleHeader onClick={() => toggleSection('tips')}>
                  <span><FaInfoCircle /> Prompt Writing Tips</span>
                  {openSections.tips ? <FaChevronUp /> : <FaChevronDown />}
                </CollapsibleHeader>
                <CollapsibleBody isOpen={openSections.tips}>
                  <HelpText>
                    <strong>Be specific about personality:</strong> "You are friendly but professional" works better than just "be nice."
                  </HelpText>
                  <HelpText>
                    <strong>Define boundaries:</strong> Clearly state what the agent should NOT do (e.g., "Never promise a specific price").
                  </HelpText>
                  <HelpText>
                    <strong>Give examples:</strong> Show the agent how to respond in tricky situations.
                  </HelpText>
                  <HelpText>
                    <strong>Keep it focused:</strong> Shorter, clear prompts perform better than very long ones. Aim for under 2000 characters for the system prompt.
                  </HelpText>
                  <HelpText>
                    <strong>Use the context tab:</strong> Put factual business info (pricing tiers, FAQs, policies) in the Business Context field so you can update it without changing the core instructions.
                  </HelpText>
                </CollapsibleBody>
              </Collapsible>
            </>
          )}

          {/* ── Tools Tab ── */}
          {promptTab === 'tools' && (
            <>
              <FormGroup>
                <Label>Available Tools & Capabilities</Label>
                <HelpText>
                  These are the tools the AI agent can use during a call. The agent decides when to use each tool based on the conversation.
                  You can reference these in your system prompt to guide when the agent should use them.
                </HelpText>

                <ToolGrid>
                  <ToolTag color="#3d5afe">create_lead</ToolTag>
                  <ToolTag color="#3d5afe">search_lead</ToolTag>
                  <ToolTag color="#27ae60">check_service_area</ToolTag>
                  <ToolTag color="#27ae60">get_services</ToolTag>
                  <ToolTag color="#f39c12">schedule_estimate</ToolTag>
                  <ToolTag color="#f39c12">get_availability</ToolTag>
                  <ToolTag color="#e74c3c">transfer_call</ToolTag>
                  <ToolTag color="#e74c3c">end_call</ToolTag>
                  <ToolTag color="#9C27B0">send_sms</ToolTag>
                  <ToolTag color="#3498db">get_business_hours</ToolTag>
                </ToolGrid>
              </FormGroup>

              <Collapsible>
                <CollapsibleHeader onClick={() => toggleSection('tools')}>
                  <span><FaTools /> Tool Descriptions</span>
                  {openSections.tools ? <FaChevronUp /> : <FaChevronDown />}
                </CollapsibleHeader>
                <CollapsibleBody isOpen={openSections.tools}>
                  <PlaceholderTable>
                    <thead>
                      <tr><th>Tool</th><th>Description</th><th>When Used</th></tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>create_lead</code></td>
                        <td>Creates a new lead in the CRM</td>
                        <td>After collecting name, phone, address, and service needed</td>
                      </tr>
                      <tr>
                        <td><code>search_lead</code></td>
                        <td>Searches for existing leads by phone or name</td>
                        <td>To check if caller is already in the system</td>
                      </tr>
                      <tr>
                        <td><code>check_service_area</code></td>
                        <td>Verifies if a zipcode is within service area</td>
                        <td>When caller provides their address/zipcode</td>
                      </tr>
                      <tr>
                        <td><code>get_services</code></td>
                        <td>Lists available services</td>
                        <td>When caller asks what services you offer</td>
                      </tr>
                      <tr>
                        <td><code>schedule_estimate</code></td>
                        <td>Books a free estimate appointment</td>
                        <td>When caller wants to schedule</td>
                      </tr>
                      <tr>
                        <td><code>get_availability</code></td>
                        <td>Checks available time slots</td>
                        <td>Before scheduling an estimate</td>
                      </tr>
                      <tr>
                        <td><code>transfer_call</code></td>
                        <td>Transfers to a human agent</td>
                        <td>When caller requests a person or agent can't help</td>
                      </tr>
                      <tr>
                        <td><code>end_call</code></td>
                        <td>Politely ends the call</td>
                        <td>When conversation is complete</td>
                      </tr>
                      <tr>
                        <td><code>send_sms</code></td>
                        <td>Sends a text message to the caller</td>
                        <td>To send confirmation or follow-up info</td>
                      </tr>
                      <tr>
                        <td><code>get_business_hours</code></td>
                        <td>Returns current business hours</td>
                        <td>When caller asks about hours</td>
                      </tr>
                    </tbody>
                  </PlaceholderTable>
                </CollapsibleBody>
              </Collapsible>

              <FormGroup>
                <Label>Custom Tool Instructions (Optional)</Label>
                <HelpText>
                  Add specific instructions for how the agent should use tools. For example:
                  "Always check_service_area before creating a lead" or "Only transfer_call if the caller asks twice."
                </HelpText>
                <TextArea
                  rows={6}
                  value={prompt.tool_instructions || ''}
                  onChange={e => setPrompt(prev => ({ ...prev, tool_instructions: e.target.value }))}
                  placeholder={`e.g.\n- Always search for existing lead before creating a new one\n- Check service area before scheduling an estimate\n- Send SMS confirmation after scheduling\n- Only transfer to human if caller explicitly asks or you cannot help`}
                />
              </FormGroup>
            </>
          )}

          {/* ── Business Context Tab ── */}
          {promptTab === 'context' && (
            <>
              <FormGroup>
                <Label>Business Context & Knowledge Base</Label>
                <HelpText>
                  Add factual information about your business that the AI should know. This is injected into the prompt as reference material.
                  Include things like: services details, pricing guidance, common FAQs, policies, seasonal info, etc.
                </HelpText>
                <TextArea
                  rows={12}
                  value={prompt.context_info || ''}
                  onChange={e => setPrompt(prev => ({ ...prev, context_info: e.target.value }))}
                  placeholder={`e.g.\nServices:\n- Asphalt paving (driveways, parking lots)\n- Sealcoating\n- Crack filling\n- Pothole repair\n- Line striping\n\nPricing:\n- We don't give exact prices over the phone\n- Free estimates for all residential jobs\n- Commercial jobs may require a site visit\n\nFAQs:\n- How long does sealcoating take? Usually 1 day, needs 24-48h to cure\n- Do you work in winter? We operate March through November\n- Payment options: Cash, check, credit card, financing available`}
                />
                <CharCount warning={(prompt.context_info || '').length > 3000}>
                  {(prompt.context_info || '').length} characters
                  {(prompt.context_info || '').length > 3000 && ' - Consider keeping under 3000 for optimal performance'}
                </CharCount>
              </FormGroup>

              <FormGroup>
                <Label>Conversation Starters / Common Scenarios</Label>
                <HelpText>
                  Define how the agent should handle specific scenarios. These help the agent respond consistently.
                </HelpText>
                <TextArea
                  rows={8}
                  value={prompt.scenarios || ''}
                  onChange={e => setPrompt(prev => ({ ...prev, scenarios: e.target.value }))}
                  placeholder={`e.g.\nIf caller asks about pricing:\n→ "I'd be happy to help! We offer free estimates. Can I get your address to check if you're in our service area?"\n\nIf caller is upset:\n→ Acknowledge their frustration, apologize, and offer to connect them with a manager\n\nIf caller asks for a specific person:\n→ "Let me transfer you right away." Use transfer_call tool.`}
                />
              </FormGroup>
            </>
          )}

          <SaveButton onClick={handleSavePrompt} disabled={saving === 'prompt'}>
            <FaSave /> {saving === 'prompt' ? 'Saving...' : 'Save Prompt Settings'}
          </SaveButton>
        </Section>
      </Container>
    </DashboardLayout>
  );
};

export default AIAgentDashboard;
